import type {
  Schemas,
  ResponseByPath,
  QueryParamsByPath,
  BodyByPath,
} from '@/api/types'
import type { IncludedResourceMap } from '@/utils/jsonapi.js'

export type ThreadIncludedMap = IncludedResourceMap<
  | 'ConversationParticipant'
  | 'ConversationMessage'
  | 'ConversationMessageReceipt'
>

type UserAttributes = NonNullable<Schemas['User']['attributes']>

export type ParticipantAttributes = NonNullable<
  Schemas['ConversationParticipant']['attributes']
>

export type ConversationListItem = Schemas['ConversationThreadList']

export const CONVERSATIONS_LIST_URL = '/{provet_id}/api/conversations/threads'
export type ConversationListResponse = ResponseByPath<
  'get',
  typeof CONVERSATIONS_LIST_URL
>
export type ConversationListQueryParams = QueryParamsByPath<
  'get',
  typeof CONVERSATIONS_LIST_URL
>

// Used to start a NEW conversation with a client. Requires `client_id` in body
// to identify the recipient and creates a new thread automatically.
export const CONVERSATION_MESSAGES_URL =
  '/{provet_id}/api/conversations/messages'
export type SendMessageBody = BodyByPath<
  'post',
  typeof CONVERSATION_MESSAGES_URL
>

// Used to send a message to an EXISTING open thread. The thread ID is in the URL,
// so no `client_id` is needed - just `channel` and `content`.
export const CONVERSATION_THREAD_MESSAGES_URL =
  '/{provet_id}/api/conversations/threads/{id}/messages'
export type SendMessageToThreadBody = BodyByPath<
  'post',
  typeof CONVERSATION_THREAD_MESSAGES_URL
>

export const CONVERSATION_THREAD_DETAIL_URL =
  '/{provet_id}/api/conversations/threads/{id}'
export type ConversationThreadDetailResponse = ResponseByPath<
  'get',
  typeof CONVERSATION_THREAD_DETAIL_URL
>
export type ConversationThreadDetailQueryParams = QueryParamsByPath<
  'get',
  typeof CONVERSATION_THREAD_DETAIL_URL
>

export const MESSAGE_CONSTRAINTS = {
  MAX_LENGTH: 1000,
  MIN_LENGTH: 1,
} as const
export type MessageConstraints = typeof MESSAGE_CONSTRAINTS

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
} as const
export type PaginationConfig = typeof PAGINATION

export const MAX_UNREAD_COUNT = 9 as const

export const ARIA_TRUNCATED_MESSAGE_LENGTH = 50 as const

export const ConversationStatus = {
  Open: 'OPEN',
  Resolved: 'RESOLVED',
} as const

export type ConversationStatusValue =
  (typeof ConversationStatus)[keyof typeof ConversationStatus]
export type ConversationStatusKey = keyof typeof ConversationStatus

export const ConversationPriority = {
  Low: 'LOW',
  Normal: 'NORMAL',
  High: 'HIGH',
} as const

export type ConversationPriorityValue =
  (typeof ConversationPriority)[keyof typeof ConversationPriority]
export type ConversationPriorityKey = keyof typeof ConversationPriority

export const MessageReceiptStatus = {
  Failed: 'failed',
  Pending: 'pending',
  Sent: 'sent',
  Delivered: 'delivered',
  Read: 'read',
} as const

export type MessageReceiptStatusKey = keyof typeof MessageReceiptStatus
export type MessageReceiptStatusValue =
  (typeof MessageReceiptStatus)[MessageReceiptStatusKey]

export const MessageDirection = {
  Inbound: 'inbound',
  Outbound: 'outbound',
} as const

export type MessageDirectionKey = keyof typeof MessageDirection
export type MessageDirectionValue =
  (typeof MessageDirection)[keyof typeof MessageDirection]

export const ConversationViewMode = {
  Viewing: 'viewing',
  New: 'new',
} as const

export type ConversationViewModeValue =
  (typeof ConversationViewMode)[keyof typeof ConversationViewMode]
export type ConversationViewModeKey = keyof typeof ConversationViewMode

export type ParticipantInfo = {
  readonly first_name: string
  readonly last_name: string
  readonly initials: string
  readonly phone_number: string
  readonly avatar: string
}

export type ParticipantType = 'employee' | 'client' | 'system' | 'bot'

export type ClientParticipant = {
  readonly participant_type: ParticipantType
  readonly participant_internal_id: number | null
  readonly created_at?: string
  readonly updated_at?: string
  readonly info: ParticipantInfo
}

export type ParticipantIncluded = {
  readonly type: 'ConversationParticipant'
  readonly id: string
  readonly attributes: ClientParticipant
}

export type MessageIncluded = {
  readonly type: 'ConversationMessage'
  readonly id: string
  readonly attributes: {
    readonly content: string
    readonly external_id: string
    readonly message_type: string
    readonly direction: string
    readonly created_at: string
    readonly updated_at: string
  }
}

export type MessageSender = {
  readonly first_name?: string
  readonly last_name?: string
  readonly phone_number?: string
}

export type MessageReceipt = {
  readonly status?: MessageReceiptStatusValue
  readonly error_code?: number | null
  readonly created_at?: string
  readonly updated_at?: string
}

export type MessageWithSender = MessageIncluded & {
  readonly sender?: MessageSender
  readonly receipts?: MessageReceipt[]
}

export type AssignedUser = Pick<
  UserAttributes,
  'first_name' | 'last_name' | 'initials'
> & {
  readonly id: string
}

type ConversationListResponseItem = ConversationListResponse['data'][number]

export type NormalizedConversationAttributes =
  ConversationListResponseItem['attributes'] & {
    readonly id: string
  }

export interface NormalizedConversation extends Omit<
  ConversationListResponseItem,
  'attributes'
> {
  readonly attributes: NormalizedConversationAttributes
  readonly client?: ClientParticipant
  readonly assignedUser?: AssignedUser
}

/**
 * Converts a thread detail response into a NormalizedConversation.
 * ConversationThreadDetail and ConversationThreadList attributes are structurally
 * identical but TypeScript treats them as distinct types since they come from
 * different API endpoint schemas, hence the cast on attributes.
 */
export function normalizeThreadDetailToConversation(
  id: string,
  attributes: NonNullable<
    ConversationThreadDetailResponse['data']['attributes']
  >,
  client?: ClientParticipant,
  assignedUser?: AssignedUser,
): NormalizedConversation {
  return {
    id,
    type: 'ConversationThread',
    attributes: {
      ...attributes,
      id,
    } as NormalizedConversationAttributes,
    client,
    assignedUser,
  }
}

/**
 * Extracts client and assigned user from thread detail participants.
 * Used when building a NormalizedConversation from thread detail data
 * (e.g., when the selected conversation is not in the current list page).
 */
export function extractParticipants(participants: ParticipantIncluded[]): {
  client?: ClientParticipant
  assignedUser?: AssignedUser
} {
  const clientParticipant = participants.find(
    (p) => p.attributes?.participant_type === 'client',
  )

  const assignedUserParticipant = participants.find(
    (p) => p.attributes?.participant_type === 'employee',
  )

  const assignedUser = assignedUserParticipant
    ? {
        id: assignedUserParticipant.id,
        first_name: assignedUserParticipant.attributes?.info?.first_name ?? '',
        last_name: assignedUserParticipant.attributes?.info?.last_name ?? '',
        initials: assignedUserParticipant.attributes?.info?.initials ?? '',
      }
    : undefined

  return {
    client: clientParticipant?.attributes,
    assignedUser,
  }
}

export type NormalizedConversationThreadResponse = {
  data: ConversationThreadDetailResponse['data']
  participants: ParticipantIncluded[]
  messages: MessageWithSender[]
}

export type NormalizedConversationListResponse = Omit<
  ConversationListResponse,
  'data'
> & {
  data: NormalizedConversation[]
}

export type ConversationListFilters = {
  search?: string
  status?: ConversationListQueryParams['filter[status]']
  priority?: ConversationListQueryParams['filter[priority]']
  client?: ConversationListQueryParams['filter[participant_client]']
}

export type ConversationMessage = NonNullable<
  Schemas['ConversationSendMessageToClientRequest']['data']['attributes']
>
